import React, { useEffect, useMemo, useState } from "react";
import { RefreshCw, Sparkles } from "lucide-react";
import { ArrowLeft } from "../AdminIcons";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAllDemoSessionRequestsQuery } from "../../services/demo-session/demoSessionApi";
import { useGetAllEnrollmentsQuery } from "../../services/enrollment/enrollmentApi";
import { useGetAllNameChangeRequestsQuery, useApproveOrRejectNameChangeRequestMutation } from "../../services/name-change/nameChangeApi";
import NameCorrectionModal from "./NameCorrectionModal";
import { getApiErrorMessage, showToast } from "../../utils/toast";

const PAGE_SIZE = 5;

const REQUEST_TABS = [
  "Students Demo Requests",
  "Students Live Session Requests",
  "Student Name Change Request",
];



const getInitials = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

const formatRequestDate = (value) => {
  if (!value) {
    return "N/A";
  }

  const dateValue = new Date(value);

  if (Number.isNaN(dateValue.getTime())) {
    return value;
  }

  return dateValue.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const getStatusTone = (status) => {
  switch (String(status || "").toLowerCase()) {
    case "pending":
      return "bg-[#FFCA2833] text-[#FFCA28]";
    case "rejected":
      return "bg-[#EF44442E] text-[#EF4444]";
    case "approved":
      return "bg-[#DFF5E5] text-[#16A34A]";
    default:
      return "bg-[#F3F4F6] text-[#374151]";
  }
};

const ManageRequestView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialTab = REQUEST_TABS.includes(location.state?.activeTab)
    ? location.state.activeTab
    : REQUEST_TABS[0];
  const [activeTab, setActiveTab] = useState(initialTab);
  const [currentPage, setCurrentPage] = useState(1);
  const [nameCorrectionRequest, setNameCorrectionRequest] = useState(null);
  const [pendingNameChangeAction, setPendingNameChangeAction] = useState(null); // "approve" | "reject" | null
  const isDemoRequestsTab = activeTab === REQUEST_TABS[0];

  useEffect(() => {
    if (REQUEST_TABS.includes(location.state?.activeTab)) {
      setActiveTab(location.state.activeTab);
      setCurrentPage(1);
    }
  }, [location.state?.activeTab]);

  const {
    data: demoSessionResponse,
    isLoading: isDemoLoading,
    error: demoError,
    refetch: refetchDemo,
  } = useGetAllDemoSessionRequestsQuery(
    {},
    {
      skip: !isDemoRequestsTab,
    },
  );

  const isLiveRequestsTab = activeTab === REQUEST_TABS[1];
  const isNameChangeTab = activeTab === REQUEST_TABS[2];

  const {
    data: enrollResponse,
    refetch: refetchEnroll,
  } = useGetAllEnrollmentsQuery(
    {
      page: currentPage,
      limit: PAGE_SIZE,
      isSessionAssigned: false,
      status: "approved",
    },
    {
      skip: !isLiveRequestsTab,
    },
  );

  const {
    data: nameChangeResponse,
    refetch: refetchNameChange,
  } = useGetAllNameChangeRequestsQuery(
    {
      page: currentPage,
      limit: PAGE_SIZE,
    },
    {
      skip: !isNameChangeTab,
    },
  );

  const [approveOrRejectNameChangeRequest, { isLoading: isUpdatingNameChange }] =
    useApproveOrRejectNameChangeRequestMutation();

  const demoSessionRows = useMemo(() => {
    const rows = demoSessionResponse?.data;

    if (!Array.isArray(rows)) {
      return [];
    }

    return rows.map((item, index) => {
      const studentName =
        [item.student?.firstName, item.student?.lastName]
          .filter(Boolean)
          .join(" ") || "N/A";
       const instructor = item.instructor;
        
      return {
        id: item._id,
        student: studentName,
        instructor,
        email: item.student?.email || "N/A",
        course: item.course?.courseTitle || "N/A",
        courseId: item.course?._id || item.course?.id || "",
        category:item.course?.courseCategory.categoryName,
        subcategory:item.course?.courseSubCategory.title,
        demoSessionLink:item.demoSessionLink,
        ageGroup:item.course?.courseAgeGroup.title,
        courseLevel:item.course?.courseLevel.title,
        preferredTime: item.preferredTime,
        preferredDate: formatRequestDate(item.preferredDate),
        requestDate: formatRequestDate(
          item.createdAt || item.requestDate || item.date,
        ),
        createdAt: item.createdAt || item.requestDate || item.date || null,
        status: item.status || item.requestStatus || "Pending",
        avatar: item.student?.profilePicture?.secureUrl || null,
      };
    });
  }, [demoSessionResponse]);

  const enrollRows = useMemo(() => {
    const rows = enrollResponse?.data;
    if (!Array.isArray(rows)) return [];

    return rows.map((item) => {
      const studentName =
        [item.user?.firstName, item.user?.lastName].filter(Boolean).join(" ") ||
        item.studentName ||
        "N/A";

      return {
        id: item._id,
        student: studentName,
        email:  item.user.email || "N/A",
        course: item.course?.courseTitle  || "N/A",
        preferredTime: item.preferredClassTime || null,
        requestDate: formatRequestDate(item.createdAt || item.requestDate || item.date || item.purchaseDate),
        createdAt: item.createdAt || item.requestDate || item.date || item.purchaseDate || null,
        status:  item.enrollmentStatus || "Pending",
        avatar: item.user?.profilePicture?.secureUrl || null,
        courseId: item.course?._id || "",
        ageGroup:item.course?.courseAgeGroup.title,
        courseLevel:item.course?.courseLevel.title,
        category:item.course?.courseCategory.categoryName,
        subcategory:item.course?.courseSubCategory.title,
        enrollmentType:item.enrollmentType,
      };
    });
  }, [enrollResponse]);

  const nameChangeRows = useMemo(() => {
    const rows = nameChangeResponse?.data;

    if (!Array.isArray(rows)) return [];

    return rows.map((item) => {
      const studentName =
        [item.createdBy?.firstName, item.createdBy?.lastName].filter(Boolean).join(" ") ||
        "N/A";

      return {
        id: item._id || "",
        student: studentName,
        email: item.createdBy?.email || "N/A",
        requestDate: formatRequestDate(item.createdAt),
        createdAt: item.createdAt || item.requestDate || item.date || null,
        status: item.isApproved === false ? "Pending" : "Approved",
        avatar: item.createdBy?.profilePicture?.secureUrl || null,
        currentName: studentName || null,
        requestedName:
          [item.firstName, item.lastName].filter(Boolean).join(" ") || "N/A",
        reason: item.reasonForCorrection || "N/A",
        firstName: item.firstName || "",
        lastName: item.lastName || "",
      };
    });
  }, [nameChangeResponse]);

  const filteredRows = useMemo(() => {
    if (isDemoRequestsTab) {
      return demoSessionRows;
    }

    if (isLiveRequestsTab) {
      return enrollRows;
    }

    if (isNameChangeTab) {
      return nameChangeRows;
    }

    return [];
  }, [
    demoSessionRows,
    enrollRows,
    nameChangeRows,
    isDemoRequestsTab,
    isLiveRequestsTab,
    isNameChangeTab,
  ]);

  const enrollPagination = useMemo(
    () => enrollResponse?.pagination || enrollResponse?.data?.pagination || {},
    [enrollResponse],
  );

  const totalPages = isLiveRequestsTab
    ? Math.max(1, enrollPagination.pages || enrollPagination.totalPages || 1)
    : Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageRows =
    isLiveRequestsTab
      ? filteredRows
      : filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const handleRefresh = () => {
    if (isDemoRequestsTab) {
      refetchDemo();
    } else if (isLiveRequestsTab) {
      refetchEnroll();
    } else if (isNameChangeTab) {
      refetchNameChange();
    }
  };

  const handleOpenRequest = (request) => {
    navigate(`/request/demo-session/${request.id}`, {
      state: {
        request,
        tab: activeTab,
      },
    });
  };

  const handleCloseNameCorrection = () => {
    setNameCorrectionRequest(null);
  };

  const handleApproveNameCorrection = async (request) => {
    if (!request?.id) {
      return;
    }

    setPendingNameChangeAction("approve");
    try {
      const response = await approveOrRejectNameChangeRequest({
        id: request.id,
        isApproved: true,
      }).unwrap();

      if (response?.success === false) {
        showToast({
          type: "error",
          message:
            response?.message || "Failed to approve name change request.",
        });
        return;
      }

      showToast({
        type: "success",
        message:
          response?.message || "Name change request approved successfully.",
      });
      setNameCorrectionRequest(null);
      refetchNameChange();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Failed to approve name change request.",
        ),
      });
    } finally {
      setPendingNameChangeAction(null);
    }
  };

  const handleRejectNameCorrection = async (request) => {
    if (!request?.id) {
      return;
    }

    setPendingNameChangeAction("reject");
    try {
      const response = await approveOrRejectNameChangeRequest({
        id: request.id,
        isApproved: false,
      }).unwrap();

      if (response?.success === false) {
        showToast({
          type: "error",
          message:
            response?.message || "Failed to reject name change request.",
        });
        return;
      }

      showToast({
        type: "success",
        message:
          response?.message || "Name change request rejected successfully.",
      });
      setNameCorrectionRequest(null);
      refetchNameChange();
    } catch (error) {
      showToast({
        type: "error",
        message: getApiErrorMessage(
          error,
          "Failed to reject name change request.",
        ),
      });
    } finally {
      setPendingNameChangeAction(null);
    }
  };

  const showNameChange = activeTab === REQUEST_TABS[2];

  return (
    <section className="min-h-screen bg-[#D9D9D914] px-7 pt-4 ">
      <div className="mx-auto max-w-[1515px]">
        <div className="rounded bg-white pb-6 pt-4">
          <div className="flex flex-wrap items-center justify-between gap-4 px-4 sm:pr-8">
            <button
              type="button"
              aria-label="Go back"
              onClick={() => window.history.back()}
            >
              <ArrowLeft />
            </button>

            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-1.5 rounded bg-[#923D8C] px-3 py-2 font-['Inter'] text-sm text-white transition hover:bg-[#82367E] sm:px-4 sm:text-base"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh Requests
            </button>
          </div>

          <div className="mt-5 flex gap-6 overflow-x-auto whitespace-nowrap px-4 font-medium leading-[19px] sm:flex-wrap sm:gap-11 sm:pl-14 sm:pr-4">
            {REQUEST_TABS.map((tab) => {
              const isActive = tab === activeTab;

              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab);
                    setCurrentPage(1);
                  }}
                  className={`relative shrink-0 pb-3 text-left transition ${isActive ? "text-[#923D8C]" : "text-black hover:text-[#9C4898]"}`}
                >
                  {tab}
                  <span
                    className={`absolute inset-x-0 bottom-1 h-1  ${isActive ? "bg-[#923D8C]" : "bg-transparent"}`}
                  />
                </button>
              );
            })}
          </div>

        </div>

        {showNameChange ? (
          <div className="mt-6 flex items-start gap-3 px-4 pb-5 sm:mt-10 sm:gap-4 sm:px-14">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#F97316] to-[#A855F7] text-white sm:h-12 sm:w-12">
              <Sparkles className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-bold leading-7 text-[#111827] sm:text-[30px] sm:leading-9">
                Certificate Name Corrections
              </h2>
              <p className="mt-1 text-sm text-[#4B5563] sm:text-base">
                Manage student certificate name correction requests
              </p>
            </div>
          </div>
        ) : null}

        <div className="mt-3 overflow-hidden rounded-2xl bg-white border border-[#BCBCBC45]">
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 text-left font-['Roboto']">
              <thead className="bg-[#B9B9B912] text-sm font-bold uppercase  text-[#000]">
                <tr>
                  {showNameChange ? (
                    <>
                      <th className="px-4 py-4 sm:px-6">Student</th>
                      <th className="px-4 py-4 sm:px-6">Current Name</th>
                      <th className="px-4 py-4 sm:px-6">Requested Name</th>
                      <th className="px-4 py-4 sm:px-6">Request Date</th>
                      <th className="px-4 py-4 sm:px-6">Status</th>
                      <th className="px-4 py-4 sm:px-6">Actions</th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-4 sm:px-6">Student</th>
                      <th className="px-4 py-4 sm:px-6">Course</th>
                      <th className="px-4 py-4 sm:px-6">Preferred Time</th>
                      <th className="px-4 py-4 sm:px-6">Request Date</th>
                      <th className="px-4 py-4 sm:px-6">Status</th>
                      <th className="px-4 py-4 sm:px-6">Actions</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {isDemoRequestsTab && demoError ? (
                  <tr>
                    <td
                      colSpan={showNameChange ? 7 : 6}
                      className="px-4 py-10 text-center text-sm text-[#B91C1C]"
                    >
                      Failed to load demo session requests.
                    </td>
                  </tr>
                ) : null}

                {showNameChange  && pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-10 text-center text-sm text-[#6B7280]">
                      No name change requests found
                    </td>
                  </tr>
                ) : null}

                {pageRows.map((request) => (
                  <tr key={request.id} className={`border-b border-[#000] `}>
                    {showNameChange ? (
                      <>
                        <td className="px-4 py-5 sm:px-6">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#F97316] to-[#A855F7] text-sm font-semibold text-white">
                              {request.avatar ? (
                                <img
                                  src={request.avatar}
                                  alt={request.student}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                getInitials(request.student)
                              )}
                            </div>
                            <div>
                              <p className="leading-[19px] text-black">
                                {request.student}
                              </p>
                              <p className="text-xs leading-[14px] text-[#878A8E]">
                                {request.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E] font-semibold text-sm">
                          {request.currentName}
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E] font-semibold text-sm">
                          {request.requestedName}
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E] font-semibold text-sm">
                          {request.requestDate}
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <span
                            className={`inline-flex capitalize rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(request.status)}`}
                          >
                            {request.status}
                          </span>
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <div className="flex flex-wrap gap-1.5">
                            {/* <button
                              type="button"
                              onClick={() => handleOpenRequest(request)}
                              className="rounded border border-[#E5E5E5] bg-white px-[26px] py-1.5 text-sm leading-4 text-black transition hover:bg-[#FAF7FC]"
                            >
                              View Detail
                            </button> */}
                            <button
                              type="button"
                              onClick={() => setNameCorrectionRequest(request)}
                              className="rounded bg-[#923D8C] px-[18px] py-1.5 text-sm text-white leading-4 transition hover:bg-[#8A3E89]"
                            >
                              Approve
                            </button>
                            <button
                              type="button"
                              onClick={() => setNameCorrectionRequest(request)}
                              className="rounded border border-[#E5E5E5] bg-white px-[14px] py-1.5 text-sm text-black transition hover:bg-[#FAF7FC]"
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-5 sm:px-6">
                          <div className="flex items-center gap-3">
                            <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#F97316] to-[#A855F7] text-sm font-semibold text-white">
                              {request.avatar ? (
                                <img
                                  src={request.avatar}
                                  alt={request.student}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                getInitials(request.student)
                              )}
                            </div>
                            <div>
                              <p className="leading-[19px] text-black">
                                {request.student}
                              </p>
                              <p className="text-xs leading-[14px] text-[#878A8E]">
                                {request.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#000] ">
                          {request.course}
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E] font-semibold text-sm">
                          {request.preferredDate} • {request.preferredTime}
                        </td>
                        <td className="px-4 py-5 sm:px-6 text-[#878A8E] font-semibold text-sm">
                          {request.requestDate}
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <span
                            className={`inline-flex capitalize rounded-full px-2 py-1 text-xs font-semibold ${getStatusTone(request.status)}`}
                          >
                            {request.status}
                          </span>
                        </td>
                        <td className="px-4 py-5 sm:px-6">
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              onClick={() => handleOpenRequest(request)}
                              className="rounded bg-[#923D8C] px-[18px] py-1.5 text-sm text-white leading-4 transition hover:bg-[#8A3E89]"
                            >
                              View
                            </button>
                         {/*   <button
                              type="button"
                              className="rounded border border-[#E5E5E5] bg-white px-[14px] py-1.5 text-sm text-black transition hover:bg-[#FAF7FC]"
                            >
                              Reject
                            </button>
                         */}
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between gap-3 px-2 text-sm text-[#1F2937]">
          <button
            type="button"
            disabled={safePage <= 1 || (isDemoRequestsTab && isDemoLoading)}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
            className="inline-flex items-center gap-2 rounded-full border border-[#D8D0DD] bg-white px-6 py-2 font-medium transition hover:bg-[#FAF7FC] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>

          <p className="text-center text-sm font-medium text-[#1F2937]">
            Page {safePage}/{totalPages}
          </p>

          <button
            type="button"
            disabled={safePage >= totalPages || (isDemoRequestsTab && isDemoLoading)}
            onClick={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
            className="inline-flex items-center gap-2 rounded-full border border-[#D8D0DD] bg-white px-6 py-2 font-medium transition hover:bg-[#FAF7FC] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </div>

      <NameCorrectionModal
        isOpen={Boolean(nameCorrectionRequest)}
        request={nameCorrectionRequest}
        onClose={handleCloseNameCorrection}
        onApprove={handleApproveNameCorrection}
        onReject={handleRejectNameCorrection}
        isApproving={isUpdatingNameChange && pendingNameChangeAction === "approve"}
        isRejecting={isUpdatingNameChange && pendingNameChangeAction === "reject"}
      />
    </section>
  );
};

export default ManageRequestView;
